import React, { useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';

import { useLotRates } from '../api/getLotRates';
import { LotRatesMatrices, SupplierRatesMatrices } from './LotRatesMatrices';
import { RateCellOverlay } from './RateCellOverlay';

export const LotRatesView = ({ resource, id, lot, procurementName }) => {
  const [facId, setFacId] = useState(null);
  const [cell, setCell] = useState(null);
  const { data: lotRates, isFetchedAfterMount } = useLotRates(resource, id, lot.lot_id);

  const perSupplier = lotRates.rates_per_supplier;
  const supplier = perSupplier && lotRates.suppliers.find(({ fac_id }) => fac_id === facId);
  const views = [
    { label: 'Summary', value: null },
    ...lotRates.suppliers.map(({ fac_id, name }) => ({ label: name, value: fac_id })),
  ];

  return (
    <Stack gap={2}>
      {perSupplier && (
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          px={2}
          py={1.5}
          bgcolor="grey.50"
          border="1px solid"
          borderColor="border.main"
          borderRadius={2}
        >
          <Typography variant="body2" fontWeight={700} color="text.secondary" mr={1}>
            {`Show rates in ${lot.name} for:`}
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            role="group"
            aria-label={`Rates in ${lot.name}`}
          >
            {views.map(({ label, value }) => {
              const selected = (supplier ? supplier.fac_id : null) === value;
              return (
                <Chip
                  key={value ?? 'summary'}
                  label={label}
                  clickable
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  aria-pressed={selected}
                  onClick={() => setFacId(value)}
                />
              );
            })}
          </Stack>
        </Stack>
      )}

      <Stack gap={0.25}>
        <Typography variant="h6" fontWeight={700} fontSize="1.125rem">
          {supplier
            ? `${lot.name} · ${supplier.name}`
            : perSupplier
              ? `${lot.name} · Summary`
              : lot.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {perSupplier && !supplier
            ? 'Lowest to highest max rate across all suppliers, in SEK/h. Select a rate to see each supplier.'
            : 'Max rates in SEK/h'}
        </Typography>
      </Stack>

      {supplier ? (
        <SupplierRatesMatrices
          key={supplier.fac_id}
          id={id}
          lotId={lot.lot_id}
          lotRates={lotRates}
          facId={supplier.fac_id}
        />
      ) : (
        <LotRatesMatrices
          lotRates={lotRates}
          isLoaded={isFetchedAfterMount}
          onCellClick={perSupplier ? (role, level) => setCell({ role, level }) : undefined}
        />
      )}

      {cell && (
        <RateCellOverlay
          id={id}
          procurementName={procurementName}
          lot={lot}
          role={cell.role}
          level={cell.level}
          onClose={() => setCell(null)}
        />
      )}
    </Stack>
  );
};
